import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Copy, GripVertical, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { contentBlockTypes } from '@/shared/api/generated/content/content';
import type { BlockTypeBody } from '@/shared/api/generated/model';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';

import styles from './BlocksInput.module.scss';
import { fromBlockFields } from './formModel';
import { FormFields } from './FormFields';
import { reveal, usePreviewFocus } from './previewFocus';

/** Один блок страницы: вид, значения полей и опознание для редактора. */
interface Block {
  id: string;
  type: string;
  data: Record<string, unknown>;
}

export interface BlocksInputProps {
  value: unknown;
  siteID: string;
  /** Ошибки от сервера с ключами вида `body.2.text`. */
  errors: Record<string, string>;
  /** Ключ поля — чтобы отобрать из ошибок свои. */
  fieldKey: string;
  /** Какие блоки разрешены полю; пусто — любые. */
  allowed: string[];
  onChange: (value: unknown) => void;
}

/**
 * Конструктор страницы: блоки с их полями, по порядку.
 *
 * Блоки правятся на месте, а не в окне на каждый: страница — это
 * последовательность, и видеть её целиком важнее. Порядок — перетаскиванием
 * за ручку; новый блок встаёт туда, где нажали «+». Как блок выглядит на
 * сайте, покажет живой предпросмотр (6.3) — отрисовывает его сайт.
 */
export function BlocksInput({
  value,
  siteID,
  errors,
  fieldKey,
  allowed,
  onChange,
}: BlocksInputProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [inserting, setInserting] = useState<number>();
  const [dragged, setDragged] = useState<number>();
  const [target, setTarget] = useState<number>();
  // Щелчок по блоку на сайте в предпросмотре: блок разворачивается и
  // показывается в форме.
  const focus = usePreviewFocus();

  useEffect(() => {
    if (focus?.block !== undefined) {
      reveal(document.querySelector(`[data-block-id="${CSS.escape(focus.block)}"]`));
    }
  }, [focus]);

  // Встроенные блоки и свои блоки окружения: схема у каждого окружения
  // своя, и Hero с прода на деве может не быть.
  const scope = useSiteScope();
  const env = kinds[scope?.env ?? 'prod'];

  const schemas = useQuery({
    queryKey: ['block-types', siteID, env],
    queryFn: async () => {
      const response = await contentBlockTypes(siteID, env);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос блоков');
      }

      return response.data.items ?? [];
    },
  });

  const blocks = asBlocks(value);
  const all = schemas.data ?? [];
  const palette = allowed.length === 0 ? all : all.filter((item) => allowed.includes(item.type));

  const add = (schema: BlockTypeBody, at: number) => {
    const next = [...blocks];

    next.splice(at, 0, { id: newID(), type: schema.type, data: {} });
    onChange(next);
  };

  const moveTo = (from: number, to: number) => {
    if (from === to || from + 1 === to) {
      return;
    }

    const next = [...blocks];
    const [moved] = next.splice(from, 1);

    if (moved === undefined) {
      return;
    }

    next.splice(to > from ? to - 1 : to, 0, moved);
    onChange(next);
  };

  const finishDrag = () => {
    setDragged(undefined);
    setTarget(undefined);
  };

  const allCollapsed = blocks.length > 0 && blocks.every((block) => collapsed[block.id] === true);

  return (
    <div className={styles['blocks']}>
      {blocks.length > 1 && (
        <button
          type="button"
          className={styles['fold-all']}
          onClick={() => {
            setCollapsed(Object.fromEntries(blocks.map((block) => [block.id, !allCollapsed])));
          }}
        >
          {allCollapsed ? 'Развернуть всё' : 'Свернуть всё — увидеть страницу целиком'}
        </button>
      )}

      {blocks.length === 0 && (
        <p className={styles['empty']}>Страница пока пуста. Добавьте первый блок.</p>
      )}

      {blocks.map((block, index) => {
        const schema = all.find((item) => item.type === block.type);
        const isCollapsed = collapsed[block.id] === true && focus?.block !== block.id;

        return (
          <div key={block.id} className={styles['slot']}>
            <InsertLine
              active={target === index && dragged !== undefined}
              onInsert={() => {
                setInserting(index);
              }}
            />

            <section
              data-block-id={block.id}
              className={`${styles['block'] ?? ''} ${dragged === index ? (styles['block--dragged'] ?? '') : ''}`}
              onDragOver={(event) => {
                if (dragged === undefined) {
                  return;
                }

                event.preventDefault();

                // Верхняя половина блока — вставить перед ним, нижняя — после.
                const box = event.currentTarget.getBoundingClientRect();

                setTarget(event.clientY < box.top + box.height / 2 ? index : index + 1);
              }}
              onDrop={(event) => {
                event.preventDefault();

                if (dragged !== undefined && target !== undefined) {
                  moveTo(dragged, target);
                }

                finishDrag();
              }}
            >
              <header className={styles['block__head']}>
                <span
                  className={styles['block__grip']}
                  draggable
                  role="img"
                  aria-label="Перетащить блок"
                  title="Перетащить"
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', block.id);
                    setDragged(index);
                  }}
                  onDragEnd={finishDrag}
                >
                  <GripVertical size={15} />
                </span>

                <button
                  type="button"
                  className={styles['block__title']}
                  aria-expanded={!isCollapsed}
                  onClick={() => {
                    setCollapsed((prev) => ({ ...prev, [block.id]: !isCollapsed }));
                  }}
                >
                  {isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                  <span>{schema?.title ?? block.type}</span>
                  {isCollapsed && (
                    <span className={styles['block__summary']}>{summary(block)}</span>
                  )}
                </button>

                <div className={styles['block__actions']}>
                  <Button
                    iconOnly
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    aria-label="Повторить блок"
                    onClick={() => {
                      const next = [...blocks];

                      // Копия получает своё опознание: два блока с одним
                      // редактор считает одним и тем же.
                      next.splice(index + 1, 0, { ...block, id: newID() });
                      onChange(next);
                    }}
                  >
                    <Copy size={15} />
                  </Button>

                  <Button
                    iconOnly
                    size="sm"
                    variant="ghost"
                    tone="danger"
                    aria-label="Удалить блок"
                    onClick={() => {
                      onChange(blocks.filter((item) => item.id !== block.id));
                    }}
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              </header>

              {!isCollapsed && (
                <div className={styles['block__body']}>
                  {schema === undefined ? (
                    <p className={styles['unknown']}>
                      Блок «{block.type}» здесь неизвестен — возможно, его убрали из «Моделей».
                      Значения сохранятся как есть.
                    </p>
                  ) : (
                    <FormFields
                      fields={fromBlockFields(schema.fields ?? [], block.id)}
                      values={block.data}
                      errors={errors}
                      path={`${fieldKey}.${String(index)}.`}
                      siteID={siteID}
                      onChange={(key, next) => {
                        onChange(
                          blocks.map((item) =>
                            item.id === block.id
                              ? { ...item, data: { ...item.data, [key]: next } }
                              : item,
                          ),
                        );
                      }}
                    />
                  )}
                </div>
              )}
            </section>
          </div>
        );
      })}

      <InsertLine
        active={target === blocks.length && dragged !== undefined}
        wide
        onInsert={() => {
          setInserting(blocks.length);
        }}
        onDragOver={() => {
          if (dragged !== undefined) {
            setTarget(blocks.length);
          }
        }}
        onDrop={() => {
          if (dragged !== undefined) {
            moveTo(dragged, blocks.length);
          }

          finishDrag();
        }}
      />

      <Dialog
        open={inserting !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setInserting(undefined);
          }
        }}
      >
        {inserting !== undefined && (
          <Palette
            palette={palette}
            onChoose={(schema) => {
              add(schema, inserting);
              setInserting(undefined);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** Полоса между блоками: «+» вставить сюда; при перетаскивании — куда встанет блок. */
function InsertLine({
  active,
  wide = false,
  onInsert,
  onDragOver,
  onDrop,
}: {
  active: boolean;
  wide?: boolean;
  onInsert: () => void;
  onDragOver?: () => void;
  onDrop?: () => void;
}) {
  return (
    <div
      className={`${styles['insert'] ?? ''} ${active ? (styles['insert--active'] ?? '') : ''} ${
        wide ? (styles['insert--wide'] ?? '') : ''
      }`}
      onDragOver={(event) => {
        if (onDragOver !== undefined) {
          event.preventDefault();
          onDragOver();
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop?.();
      }}
    >
      <button
        type="button"
        className={styles['insert__button']}
        aria-label="Вставить блок сюда"
        onClick={onInsert}
      >
        <Plus size={14} />
        {wide && 'Добавить блок'}
      </button>
    </div>
  );
}

/** Выбор блока: поиск, свои блоки окружения — отдельно от встроенных. */
function Palette({
  palette,
  onChoose,
}: {
  palette: BlockTypeBody[];
  onChoose: (schema: BlockTypeBody) => void;
}) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const found = palette.filter(
    (item) =>
      query === '' ||
      item.title.toLowerCase().includes(query) ||
      item.hint.toLowerCase().includes(query),
  );

  const groups = [
    { title: 'Свои блоки', items: found.filter((item) => item.type_id != null) },
    { title: 'Встроенные', items: found.filter((item) => item.type_id == null) },
  ].filter((group) => group.items.length > 0);

  return (
    <DialogContent title="Добавить блок" size="md">
      <div className={styles['palette']}>
        <Input
          autoFocus
          placeholder="Поиск блока"
          aria-label="Поиск блока"
          prefix={<Search size={15} />}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />

        {groups.length === 0 && <p className={styles['empty']}>Такого блока нет.</p>}

        {groups.map((group) => (
          <section key={group.title} className={styles['palette__group']}>
            <h3 className={styles['palette__heading']}>{group.title}</h3>
            <ul className={styles['palette__list']}>
              {group.items.map((schema) => (
                <li key={schema.type}>
                  <button
                    type="button"
                    className={styles['palette__item']}
                    onClick={() => {
                      onChoose(schema);
                    }}
                  >
                    <span className={styles['palette__title']}>{schema.title}</span>
                    {/* Названия человеку мало: «Врезка» не говорит, когда её
                        применять. */}
                    <span className={styles['palette__hint']}>{schema.hint}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </DialogContent>
  );
}

/** Начало текста блока — чтобы свёрнутая страница читалась. */
function summary(block: Block): string {
  for (const value of Object.values(block.data)) {
    if (typeof value === 'string' && value.trim() !== '' && !/^[0-9a-f-]{36}$/.test(value)) {
      const text = value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return text.length > 60 ? `${text.slice(0, 60)}…` : text;
    }
  }

  return '';
}

function asBlocks(value: unknown): Block[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (typeof item !== 'object' || item === null) {
      return [];
    }

    const raw = item as Record<string, unknown>;

    return [
      {
        id: typeof raw['id'] === 'string' ? raw['id'] : newID(),
        type: typeof raw['type'] === 'string' ? raw['type'] : '',
        data: (raw['data'] ?? {}) as Record<string, unknown>,
      },
    ];
  });
}

function newID(): string {
  return crypto.randomUUID();
}
